import { Popover } from "antd";
import { createContext, useContext, useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

/** How a Term's "More in the glossary" link opens the glossary page (the shell passes its router). */
export const TermLinkContext = createContext<((id: string) => void) | null>(null);

/** True when the glossary namespace defines `id`. */
export function useHasTerm(id: string): boolean {
  const { i18n } = useTranslation("glossary");
  return i18n.exists(`glossary:terms.${id}.term`);
}

/**
 * A concept word with a plain-language explanation (ADR-0055): dotted underline, a button that
 * opens a small popover with the glossary definition. Focus moves into the popover; Esc closes it
 * and returns focus to the word.
 */
export function Term({ id, children }: { id: string; children?: ReactNode }) {
  const { t } = useTranslation("glossary");
  const openGlossary = useContext(TermLinkContext);
  const [open, setOpen] = useState(false);
  const button = useRef<HTMLButtonElement>(null);
  const uid = useId();
  const titleId = `${uid}-title`;
  const bodyId = `${uid}-body`;
  const term = t(`terms.${id}.term`);
  const dialogId = `${uid}-dialog`;
  // Move focus into the popover once it is shown (it renders in a portal).
  useEffect(() => {
    if (!open) return;
    let tries = 0;
    let raf = 0;
    const focus = () => {
      const el = document.getElementById(dialogId);
      if (el && el.offsetParent !== null) el.focus({ preventScroll: true });
      else if (tries++ < 20) raf = requestAnimationFrame(focus);
    };
    raf = requestAnimationFrame(focus);
    return () => cancelAnimationFrame(raf);
  }, [open, dialogId]);
  const close = () => {
    setOpen(false);
    button.current?.focus();
  };
  const onKey = (e: KeyboardEvent) => {
    if (e.key === "Escape" && open) {
      e.stopPropagation();
      close();
    }
  };
  const content = (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    <div
      role="dialog"
      aria-labelledby={titleId}
      aria-describedby={bodyId}
      tabIndex={-1}
      className="ap-term-pop"
      onKeyDown={onKey}
      id={dialogId}
      data-testid={`term-pop-${id}`}
    >
      <div id={titleId} className="ap-term-pop-title">
        {term}
      </div>
      <p id={bodyId} className="ap-term-pop-body">
        {t(`terms.${id}.definition`)}
      </p>
      {openGlossary ? (
        <button
          type="button"
          className="ap-linklike"
          onClick={() => {
            setOpen(false);
            openGlossary(id);
          }}
        >
          {t("ui.more")}
        </button>
      ) : null}
    </div>
  );
  return (
    <Popover content={content} open={open} onOpenChange={setOpen} trigger="click" placement="bottom" destroyOnHidden>
      <button
        ref={button}
        type="button"
        className="ap-term"
        aria-expanded={open}
        aria-haspopup="dialog"
        onKeyDown={onKey}
        data-term={id}
      >
        {children ?? term}
        <span className="ap-visually-hidden"> {t("ui.explain")}</span>
      </button>
    </Popover>
  );
}
